import { useState, useEffect, useContext, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiCalendar, FiAlertCircle, FiX, FiChevronUp, FiChevronDown, FiCheck, FiX as FiXIcon, FiPackage } from 'react-icons/fi';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import UserContext from '../../Components/UserContext';
import ErrorBoundary from '../../Components/ErrorBoundary';

function Programs() {
  const { session } = useContext(UserContext);
  const [programs, setPrograms] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  
  // Get services and partner types
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';
  
  const [newProgram, setNewProgram] = useState({
    name: '',
    duration: '',
    durationUnit: 'weeks',
    classesPerDuration: 1,
    description: '',
    effectiveDate: '',
    isActive: true,
    service: activeService || '',
    price: 0,
    // Consultant-specific fields
    timeWindowStart: '09:00',
    timeWindowEnd: '17:00',
    slotDurations: ['1 hour']
  });
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [dateTimeSettings, setDateTimeSettings] = useState({
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12-hour'
  });
  const [filters, setFilters] = useState({
    status: 'All',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });
  const [isPWA, setIsPWA] = useState(false);
  const dateInputRef = useRef(null);
  const editFormRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await HttpInterceptor.get('/api/trainer-settings');
        setDateTimeSettings({
          dateFormat: response.data.data.dateFormat || 'MM/DD/YYYY',
          timeFormat: response.data.data.timeFormat || '12-hour'
        });
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      }
    };

    if (session?.id) {
      fetchPrograms();
      fetchSettings();
    }
  }, [session]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (dateTimeSettings.dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      default: return dateString;
    }
  };

  const handleManualDateChange = (e) => {
    const value = e.target.value;
    let date;
    
    if (dateTimeSettings.dateFormat === 'DD/MM/YYYY') {
      const [day, month, year] = value.split('/');
      if (day && month && year) {
        date = new Date(`${year}-${month}-${day}`);
      }
    } else if (dateTimeSettings.dateFormat === 'MM/DD/YYYY') {
      const [month, day, year] = value.split('/');
      if (month && day && year) {
        date = new Date(`${year}-${month}-${day}`);
      }
    } else if (dateTimeSettings.dateFormat === 'YYYY-MM-DD') {
      date = new Date(value);
    }

    if (date && !isNaN(date.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0, 0);
      
      // Only allow dates from today onwards
      if (selectedDate >= today) {
        setNewProgram({...newProgram, effectiveDate: date.toISOString().split('T')[0]});
      } else {
        toast.error('Effective date cannot be in the past. Please select today or a future date.');
        // Reset to today if past date is entered
        setNewProgram({...newProgram, effectiveDate: today.toISOString().split('T')[0]});
      }
    } else {
      setNewProgram({...newProgram, effectiveDate: value});
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (dateTimeSettings.dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      default: return date.toISOString().split('T')[0];
    }
  };

  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await HttpInterceptor.get('/api/programs', {
        params: {
          trainerId: session?.id
        }
      });
      // Debug: Log first program to see structure
      if (data && data.length > 0) {
        console.log('Sample program data:', {
          name: data[0].name,
          duration: data[0].duration,
          durationUnit: data[0].durationUnit,
          classesPerDuration: data[0].classesPerDuration,
          fullData: data[0]
        });
      }
      setPrograms(data);
    } catch (err) {
      setError(err);
      console.error('Fetch programs error:', err);
      if (err.response?.status === 401) {
        toast.error('Please log in to view programs');
      } else if (err.response?.status === 500) {
        toast.error('We’re having trouble loading programs right now. Please try again in a moment.');
      } else {
        toast.error(`Failed to fetch programs: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (duration, unit, classesPerDuration) => {
    const durationText = `${duration} ${unit}${duration > 1 ? '' : ''}`;
    return `${classesPerDuration} class${classesPerDuration > 1 ? 'es' : ''} in ${durationText}`;
  };

  const handleAdd = async () => {
    // Basic validation - name and effective date are always required
    if (!newProgram.name || !newProgram.effectiveDate) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate instructor-specific fields (Duration, Unit, Classes)
    if (!isConsultant) {
      if (!newProgram.duration || !newProgram.durationUnit || !newProgram.classesPerDuration) {
        toast.error('Please fill all required fields (Duration, Unit, and Classes)');
        return;
      }
    }
    
    // Validate consultant-specific fields
    if (isConsultant) {
      if (!newProgram.timeWindowStart || !newProgram.timeWindowEnd) {
        toast.error('Please set time window for consultant programs');
        return;
      }
      if (!newProgram.slotDurations || newProgram.slotDurations.length === 0) {
        toast.error('Please select at least one slot duration');
        return;
      }
    }
    
    if (!newProgram.service) {
      toast.error('Please select a service');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Prepare program data with correct format for backend
      const programData = {
        name: newProgram.name,
        description: newProgram.description || '',
        effectiveDate: newProgram.effectiveDate,
        price: Number(newProgram.price || newProgram.currentPrice || newProgram.basePrice || 0),
        service: newProgram.service,
        isActive: newProgram.isActive !== undefined ? newProgram.isActive : true
      };
      
      // Add instructor-specific fields (Duration, Unit, Classes) - only for instructor programs
      if (!isConsultant) {
        programData.duration = Number(newProgram.duration);
        programData.classesPerDuration = Number(newProgram.classesPerDuration || 1);
        programData.durationUnit = newProgram.durationUnit || 'weeks';
      }
      
      // Add consultant-specific fields if consultant
      if (isConsultant) {
        programData.timeWindowStart = newProgram.timeWindowStart;
        programData.timeWindowEnd = newProgram.timeWindowEnd;
        programData.slotDurations = newProgram.slotDurations;
      }

      if (isEditing !== null) {
        const { data } = await HttpInterceptor.put(`/api/programs/${isEditing}`, programData);
        // Refresh programs to get updated end date status
        const { data: updatedPrograms } = await HttpInterceptor.get('/api/programs', {
          params: {
            trainerId: session?.id
          }
        });
        setPrograms(updatedPrograms);
        toast.success('Program updated successfully');
      } else {
        const { data } = await HttpInterceptor.post('/api/programs', programData);
        setPrograms([...programs, data]);
        toast.success('Program added successfully');
      }
      resetForm();
    } catch (err) {
      setError(err);
      toast.error(err.response?.data?.message || 'Operation failed. Please try again.');
      console.error('Add/Edit program error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (program) => {
    // Use the program's actual fields (they're stored as numbers and strings in the backend)
    setNewProgram({
      name: program.name || '',
      duration: program.duration ? program.duration.toString() : '1',
      durationUnit: program.durationUnit || 'weeks', // Ensure plural: 'weeks', 'months', 'years'
      classesPerDuration: program.classesPerDuration || 1,
      description: program.description || '',
      effectiveDate: program.effectiveDate ? (typeof program.effectiveDate === 'string' ? program.effectiveDate.split('T')[0] : new Date(program.effectiveDate).toISOString().split('T')[0]) : '',
      isActive: program.isActive !== undefined ? program.isActive : true,
      service: program.service || activeService || '',
      price: program.price || program.currentPrice || program.basePrice || 0,
      // Consultant-specific fields
      timeWindowStart: program.timeWindowStart || '09:00',
      timeWindowEnd: program.timeWindowEnd || '17:00',
      slotDurations: program.slotDurations || ['1 hour']
    });
    setIsEditing(program._id);
    setShowForm(true);
    
    // Scroll to edit form after state update
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const checkDependencies = async (programId) => {
    try {
      const response = await HttpInterceptor.get(`/api/programs/${programId}/dependencies`);
      return response.data;
    } catch (error) {
      console.error('Error checking dependencies:', error);
      return null;
    }
  };

  const handleDeleteClick = async (id) => {
    // Check for dependencies first
    const dependencies = await checkDependencies(id);
    if (dependencies && dependencies.hasDependencies) {
      setDeleteDependencies(dependencies);
      setShowDeleteConfirm(id);
      return;
    }
    // No dependencies, proceed with normal delete confirmation
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async (id, force = false) => {
    try {
      setIsLoading(true);
      const url = force ? `/api/programs/${id}?force=true` : `/api/programs/${id}`;
      await HttpInterceptor.delete(url);
      setPrograms(programs.filter(p => p._id !== id));
      toast.success('Program deleted successfully');
      setShowDeleteConfirm(null);
      setDeleteDependencies(null);
      setShowForceDeleteConfirm(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete program. Please try again.';
      if (err.response?.data?.dependencies) {
        setDeleteDependencies({
          hasDependencies: true,
          dependencies: err.response.data.dependencies,
          message: err.response.data.message
        });
      } else {
        toast.error(errorMsg);
      }
      console.error('Delete program error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateProgramEndDate = (program) => {
    if (!program.effectiveDate || !program.duration || !program.durationUnit) {
      return null;
    }
    
    const startDate = new Date(program.effectiveDate);
    const endDate = new Date(startDate);
    const durationValue = Number(program.duration);
    
    if (program.durationUnit === 'weeks') {
      endDate.setDate(endDate.getDate() + (durationValue * 7));
    } else if (program.durationUnit === 'months') {
      endDate.setMonth(endDate.getMonth() + durationValue);
    } else if (program.durationUnit === 'years') {
      endDate.setFullYear(endDate.getFullYear() + durationValue);
    }
    
    return endDate;
  };

  const hasProgramEnded = (program) => {
    const endDate = calculateProgramEndDate(program);
    if (!endDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return today > endDate;
  };

  const toggleActiveStatus = async (id) => {
    try {
      setIsLoading(true);
      const program = programs.find(p => p._id === id);
      
      // Check if program has ended
      if (hasProgramEnded(program) && !program.isActive) {
        toast.error('Cannot activate a program that has ended. Please update the effective date to a future date to offer this program again.');
        return;
      }
      
      const { data } = await HttpInterceptor.patch(`/api/programs/${id}/toggle-status`, {
        isActive: !program.isActive
      });
      setPrograms(programs.map(p => p._id === id ? data : p));
      toast.success(`Program ${!program.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update status. Please try again.';
      toast.error(errorMsg);
      console.error('Toggle status error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setNewProgram({
      name: '',
      duration: '',
      durationUnit: 'weeks',
      classesPerDuration: 1,
      description: '',
      effectiveDate: '',
      isActive: true,
      service: activeService || '',
      price: 0,
      // Consultant-specific fields
      timeWindowStart: '09:00',
      timeWindowEnd: '17:00',
      slotDurations: ['1 hour']
    });
    setIsEditing(null);
    setShowForm(false);
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredItems = () => {
    let filtered = [...programs];
    
    // Apply filters
    if (filters.status !== 'All') {
      filtered = filtered.filter(program => 
        filters.status === 'Active' ? program.isActive : !program.isActive
      );
    }
    
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(program => 
        program.name.toLowerCase().includes(searchTerm) ||
        (program.description && program.description.toLowerCase().includes(searchTerm)));
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  };

  const sortedAndFilteredPrograms = getSortedAndFilteredItems();

  return (
    <ErrorBoundary>
      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
        {/* Hero Section */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} rounded-2xl ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'} overflow-hidden`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
                  <FiPackage className={isPWA ? 'text-orange-600' : 'text-white'} />
                  Programs
                </h1>
                <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'}`}>
                  {isConsultant ? 'Manage your consultation programs' : 'Manage your training programs'}
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} px-4 py-2 ${isPWA ? 'px-5 py-2.5' : ''} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'}`}
              >
                <FiPlus size={18} />
                Add Program
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className={`mb-6 p-4 ${isPWA ? 'bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200/50' : 'bg-red-50'} text-red-700 rounded-xl ${isPWA ? 'shadow-lg' : 'shadow-sm'} border ${isPWA ? '' : 'border-red-200'} flex items-start gap-3`}>
            <FiAlertCircle className="mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Error loading programs</h3>
              <p className="text-sm">{error.message || 'Unknown error occurred'}</p>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} p-4 ${isPWA ? 'rounded-2xl' : ''} mb-6`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                placeholder="Search programs..."
                className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
              >
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {showForm && (
          <div ref={editFormRef} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} p-6 ${isPWA ? 'rounded-2xl' : ''} mb-8`}>
            <h2 className={`text-lg font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-4`}>
              {isEditing !== null ? 'Edit Program' : 'Add New Program'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Service*</label>
                <select
                  value={newProgram.service}
                  onChange={(e) => setNewProgram({...newProgram, service: e.target.value})}
                  className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                  required
                >
                  <option value="">Select Service</option>
                  {trainerServices.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Program Name*</label>
                <input
                  type="text"
                  value={newProgram.name}
                  onChange={(e) => setNewProgram({...newProgram, name: e.target.value})}
                  placeholder="e.g. Yoga Basics"
                  className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                  required
                />
              </div>
              
              {/* Duration, Unit, and Classes - Only for Instructor programs, not for Consultant */}
              {!isConsultant && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Duration*</label>
                  <input
                    type="number"
                    min="1"
                    value={newProgram.duration}
                    onChange={(e) => setNewProgram({...newProgram, duration: e.target.value})}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Unit*</label>
                  <select
                    value={newProgram.durationUnit}
                    onChange={(e) => setNewProgram({...newProgram, durationUnit: e.target.value})}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                  >
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Classes*</label>
                  <input
                    type="number"
                    min="1"
                    value={newProgram.classesPerDuration}
                    onChange={(e) => setNewProgram({...newProgram, classesPerDuration: e.target.value})}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                </div>
              </div>
              )}
              <div className="relative">
                <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                  Effective Date* (Format: {dateTimeSettings.dateFormat})
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatDateForInput(newProgram.effectiveDate)}
                    onChange={handleManualDateChange}
                    placeholder={dateTimeSettings.dateFormat}
                    className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    required
                  />
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    ref={dateInputRef}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = new Date().toISOString().split('T')[0];
                      // Only allow dates from today onwards
                      if (selectedDate >= today) {
                        setNewProgram({...newProgram, effectiveDate: selectedDate});
                      } else {
                        toast.error('Effective date cannot be in the past. Please select today or a future date.');
                      }
                    }}
                  />
                  <div 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                    onClick={() => dateInputRef.current?.showPicker()}
                  >
                    <FiCalendar className={isPWA ? 'text-orange-400 hover:text-orange-600' : 'text-gray-400 hover:text-orange-600'} />
                  </div>
                </div>
                <p className={`mt-1 text-xs ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>Select today or a future date</p>
              </div>            
            </div>
            
            {/* Consultant-specific fields */}
            {isConsultant && newProgram.service && servicePartnerTypes[newProgram.service] === 'consultant' && (
              <div className="border-t pt-4 mt-4">
                <h3 className={`text-md font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-4`}>Appointment Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Time Window Start*</label>
                    <input
                      type="time"
                      value={newProgram.timeWindowStart}
                      onChange={(e) => setNewProgram({...newProgram, timeWindowStart: e.target.value})}
                      className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Time Window End*</label>
                    <input
                      type="time"
                      value={newProgram.timeWindowEnd}
                      onChange={(e) => setNewProgram({...newProgram, timeWindowEnd: e.target.value})}
                      className={`w-full border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-2`}>Available Slot Durations*</label>
                    <div className="flex flex-wrap gap-2">
                      {['30 mins', '45 mins', '1 hour', '1.5 hours', '2 hours', '3 hours'].map((duration) => (
                        <label key={duration} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newProgram.slotDurations.includes(duration)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewProgram({
                                  ...newProgram,
                                  slotDurations: [...newProgram.slotDurations, duration]
                                });
                              } else {
                                setNewProgram({
                                  ...newProgram,
                                  slotDurations: newProgram.slotDurations.filter(d => d !== duration)
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className={`text-sm ${isPWA ? 'text-orange-700' : 'text-gray-700'}`}>{duration}</span>
                        </label>
                      ))}
                    </div>
                    <p className={`mt-1 text-xs ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>Select at least one slot duration</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleAdd}
                disabled={
                  !newProgram.name || 
                  !newProgram.duration || 
                  !newProgram.effectiveDate || 
                  isLoading
                }
                className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700'} text-white px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} ${
                  (!newProgram.name || 
                   !newProgram.duration || 
                   !newProgram.effectiveDate || 
                   isLoading) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Processing...' : isEditing !== null ? 'Update Program' : 'Add Program'}
              </button>
              
              <button
                onClick={resetForm}
                disabled={isLoading}
                className={`${isPWA ? 'text-orange-700 hover:text-orange-900 hover:bg-orange-50' : 'text-gray-600 hover:text-gray-800'} px-4 py-2 rounded-lg transition-colors ${isPWA ? 'font-medium' : ''}`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} overflow-hidden`}>
          {isLoading && programs.length === 0 ? (
            <div className="text-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-4 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading programs...</p>
            </div>
          ) : sortedAndFilteredPrograms.length > 0 ? (
            <div className="p-4 sm:p-6">
              {/* Sorting Controls for Card View */}
              <div className={`mb-4 flex flex-wrap items-center gap-2 text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                <span className="font-medium">Sort by:</span>
                <button
                  onClick={() => requestSort('name')}
                  className={`px-3 py-1 rounded-md transition-all duration-200 ${
                    sortConfig.key === 'name'
                      ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'hover:bg-orange-50 text-orange-600' : 'hover:bg-gray-100'
                  }`}
                >
                  Name
                  {sortConfig.key === 'name' && (
                    sortConfig.direction === 'ascending' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
                <button
                  onClick={() => requestSort('effectiveDate')}
                  className={`px-3 py-1 rounded-md transition-all duration-200 ${
                    sortConfig.key === 'effectiveDate'
                      ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'hover:bg-orange-50 text-orange-600' : 'hover:bg-gray-100'
                  }`}
                >
                  Date
                  {sortConfig.key === 'effectiveDate' && (
                    sortConfig.direction === 'ascending' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
              </div>

              {/* Card Grid View - Responsive for all screen sizes */}
              <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                {sortedAndFilteredPrograms.map((program) => {
                  const programEnded = hasProgramEnded(program);
                  const canToggle = !programEnded || program.isActive;
                  const durationText = (() => {
                    if (program.durationUnit && program.classesPerDuration) {
                      const duration = program.duration || 1;
                      return formatDuration(duration, program.durationUnit, program.classesPerDuration);
                    }
                    if (program.duration && typeof program.duration === 'string') {
                      return program.duration;
                    }
                    if (program.duration && typeof program.duration === 'number') {
                      return `${program.duration} ${program.durationUnit || 'weeks'}`;
                    }
                    return 'N/A';
                  })();

                  return (
                    <div key={program._id} className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-lg hover:shadow-xl' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'} rounded-xl p-5 transition-all duration-200 flex flex-col ${isPWA ? 'backdrop-blur-sm' : ''}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 pr-3">
                          <h3 className={`text-lg font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-1`}>{program.name}</h3>
                          {program.description && (
                            <p className={`text-sm ${isPWA ? 'text-orange-700/80' : 'text-gray-600'} line-clamp-2 mt-1`}>{program.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Modern Toggle Switch Section */}
                      <div className={`mb-4 pb-4 border-b ${isPWA ? 'border-orange-200/50' : 'border-gray-100'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${isPWA ? 'text-orange-700' : 'text-gray-700'}`}>Status:</span>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              program.isActive 
                                ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 shadow-sm' : 'bg-green-100 text-green-800'
                                : programEnded
                                  ? isPWA ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-500'
                                  : isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 shadow-sm' : 'bg-red-100 text-red-800'
                            }`}>
                              {program.isActive ? 'Active' : programEnded ? 'Ended' : 'Inactive'}
                            </span>
                          </div>
                          
                          {/* Modern Toggle Switch */}
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium transition-colors ${
                              program.isActive ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              Off
                            </span>
                            <button
                              onClick={() => !isLoading && canToggle && toggleActiveStatus(program._id)}
                              disabled={isLoading || !canToggle}
                              title={programEnded && !program.isActive 
                                ? 'Program has ended. Update effective date to reactivate.' 
                                : program.isActive ? 'Click to deactivate' : 'Click to activate'}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                                program.isActive 
                                  ? 'bg-orange-600' 
                                  : programEnded
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-gray-300'
                              } ${isLoading || !canToggle ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                  program.isActive ? 'translate-x-6' : 'translate-x-1'
                                } ${programEnded ? 'opacity-60' : ''}`}
                              >
                                {program.isActive ? (
                                  <FiCheck className="h-3 w-3 text-orange-600 m-0.5" />
                                ) : programEnded ? (
                                  <FiXIcon className="h-3 w-3 text-gray-400 m-0.5" />
                                ) : (
                                  <FiXIcon className="h-3 w-3 text-gray-400 m-0.5" />
                                )}
                              </span>
                            </button>
                            <span className={`text-xs font-medium transition-colors ${
                              !program.isActive ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              On
                            </span>
                          </div>
                        </div>
                        {!programEnded && canToggle && (
                          <p className="text-xs text-gray-500 mt-2 ml-1">
                            {program.isActive ? 'Click toggle to deactivate this program' : 'Click toggle to activate this program'}
                          </p>
                        )}
                        {programEnded && !program.isActive && (
                          <p className="text-xs text-amber-600 mt-2 ml-1 flex items-center gap-1">
                            <FiAlertCircle size={12} />
                            Program has ended. Update effective date to reactivate.
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-2 mb-4 flex-grow">
                        <div className={`flex items-center text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                          <FiClock className={`mr-2 ${isPWA ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0`} size={16} />
                          <span className="font-medium mr-1">Duration:</span>
                          <span>{durationText}</span>
                        </div>
                        <div className={`flex items-center text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                          <FiCalendar className={`mr-2 ${isPWA ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0`} size={16} />
                          <span className="font-medium mr-1">Effective Date:</span>
                          <span>{formatDateDisplay(program.effectiveDate)}</span>
                        </div>
                      </div>

                      <div className={`flex gap-2 pt-3 border-t ${isPWA ? 'border-orange-200/50' : 'border-gray-100'} mt-auto`}>
                        <button
                          onClick={() => handleEdit(program)}
                          disabled={isLoading}
                          className={`flex-1 flex items-center justify-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg' : 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'} px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <FiEdit2 size={16} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(program._id)}
                          disabled={isLoading}
                          className={`flex-1 flex items-center justify-center gap-2 ${isPWA ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-md hover:shadow-lg' : 'text-rose-600 hover:text-rose-900 hover:bg-rose-50'} px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <FiTrash2 size={16} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className={`mx-auto h-24 w-24 ${isPWA ? 'text-orange-300' : 'text-gray-400'}`}>
                <FiCalendar size={96} className={isPWA ? 'opacity-40' : 'opacity-20'} />
              </div>
              <h3 className={`mt-2 text-lg font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'}`}>
                {filters.status !== 'All' || filters.search ? 
                  'No programs match your filters' : 
                  'No programs created yet'}
              </h3>
              <p className={`mt-1 text-sm ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>
                {filters.status !== 'All' || filters.search ? 
                  'Try adjusting your filters' : 
                  'Get started by adding your first program'}
              </p>
              {!filters.search && filters.status === 'All' && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className={`mt-4 flex items-center gap-2 mx-auto ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'}`}
                >
                  <FiPlus size={18} />
                  Add Your First Program
                </button>
              )}
            </div>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-2xl' : 'bg-white rounded-lg'} p-6 max-w-md w-full ${isPWA ? 'rounded-2xl' : ''}`}>
              {deleteDependencies && deleteDependencies.hasDependencies ? (
                <>
                  <div className="flex items-start mb-4">
                    <FiAlertCircle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Cannot Delete Program</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {deleteDependencies.message || `This program has dependencies that must be removed first.`}
                      </p>
                      {deleteDependencies.dependencies && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                          <p className="text-sm font-medium text-yellow-800 mb-2">Dependencies found:</p>
                          <ul className="text-sm text-yellow-700 space-y-1">
                            {deleteDependencies.dependencies.classes > 0 && (
                              <li className="flex items-center">
                                <FiPackage className="mr-2" />
                                {deleteDependencies.dependencies.classes} active class{deleteDependencies.dependencies.classes !== 1 ? 'es' : ''}
                              </li>
                            )}
                            {deleteDependencies.dependencies.students > 0 && (
                              <li className="flex items-center">
                                <FiPackage className="mr-2" />
                                {deleteDependencies.dependencies.students} enrolled student{deleteDependencies.dependencies.students !== 1 ? 's' : ''}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(null);
                        setDeleteDependencies(null);
                      }}
                      disabled={isLoading}
                      className="text-gray-600 hover:text-gray-800 px-4 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowForceDeleteConfirm(true)}
                      disabled={isLoading}
                      className={`px-4 py-2 text-sm font-medium ${isPWA ? 'text-orange-700 bg-orange-50 border-2 border-orange-200 hover:bg-orange-100 shadow-md' : 'text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200'} rounded-md transition-all duration-200 ${isPWA ? 'font-medium' : ''}`}
                    >
                      Force Delete
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
                  <p className="text-gray-600 mb-6">Are you sure you want to delete this program? This action cannot be undone.</p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(null);
                        setDeleteDependencies(null);
                      }}
                      disabled={isLoading}
                      className="text-gray-600 hover:text-gray-800 px-4 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmDelete(showDeleteConfirm)}
                      disabled={isLoading}
                      className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 shadow-lg hover:shadow-xl' : 'bg-rose-600 hover:bg-rose-700'} text-white px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isLoading ? 'Deleting...' : 'Delete Program'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Force Delete Confirmation */}
        {showForceDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-red-50/30 border-2 border-red-200/50 shadow-2xl' : 'bg-white rounded-lg'} p-6 max-w-md w-full ${isPWA ? 'rounded-2xl' : ''}`}>
              <div className="flex items-start mb-4">
                <FiAlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Force Delete Program</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will permanently delete the program and may affect {deleteDependencies?.dependencies?.classes || 0} classes and {deleteDependencies?.dependencies?.students || 0} students. This action cannot be undone.
                  </p>
                  <p className="text-sm font-medium text-red-600">
                    Are you absolutely sure you want to proceed?
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowForceDeleteConfirm(false);
                  }}
                  disabled={isLoading}
                  className="text-gray-600 hover:text-gray-800 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmDelete(showDeleteConfirm, true);
                    setShowForceDeleteConfirm(false);
                  }}
                  disabled={isLoading}
                  className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-lg hover:shadow-xl' : 'bg-red-600 hover:bg-red-700'} text-white px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Deleting...' : 'Yes, Force Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default Programs;